import { useState, useEffect } from 'react';
import { useForm } from '@refinedev/antd';
import { Create } from '@refinedev/antd';
import { useCustom } from '@refinedev/core';
import {
  Form,
  Input,
  InputNumber,
  Card,
  Row,
  Col,
  Select,
  Typography,
} from 'antd';
import { LabelWithHelp, SectionTitle } from '../../components/ui/HelpTooltip';
import { t } from '../../i18n';

const { Text } = Typography;

interface Region {
  id: number;
  nameUz: string;
}

interface District {
  id: number;
  nameUz: string;
  regionId: number;
}

interface User {
  id: number;
  fullName: string;
  phone: string;
}

export const TripCreate = () => {
  const [pickupRegionId, setPickupRegionId] = useState<number | null>(null);
  const [destinationRegionId, setDestinationRegionId] = useState<number | null>(null);

  const { formProps, saveButtonProps, form } = useForm({
    resource: 'admin/trips',
    redirect: 'list',
  });

  // Fetch regions
  const { data: regionsData } = useCustom<Region[]>({
    url: '/admin/regions',
    method: 'get',
  });

  // Fetch districts for pickup
  const { data: pickupDistrictsData } = useCustom<District[]>({
    url: '/admin/districts',
    method: 'get',
    config: {
      query: pickupRegionId ? { regionId: pickupRegionId } : {},
    },
    queryOptions: {
      enabled: !!pickupRegionId,
    },
  });

  // Fetch districts for destination
  const { data: destinationDistrictsData } = useCustom<District[]>({
    url: '/admin/districts',
    method: 'get',
    config: {
      query: destinationRegionId ? { regionId: destinationRegionId } : {},
    },
    queryOptions: {
      enabled: !!destinationRegionId,
    },
  });

  // Fetch users for client selection
  const { data: usersData } = useCustom<{ data: User[] }>({
    url: '/admin/users',
    method: 'get',
    config: {
      query: { limit: 100 },
    },
  });

  // Fetch drivers for driver selection
  const { data: driversData } = useCustom<{ data: User[] }>({
    url: '/admin/drivers',
    method: 'get',
    config: {
      query: { limit: 100 },
    },
  });

  const regions = regionsData?.data || [];
  const pickupDistricts = pickupDistrictsData?.data || [];
  const destinationDistricts = destinationDistrictsData?.data || [];
  const users = usersData?.data?.data || [];
  const drivers = driversData?.data?.data || [];

  // Reset district when region changes
  useEffect(() => {
    form.setFieldValue('pickupDistrictId', undefined);
  }, [pickupRegionId, form]);

  useEffect(() => {
    form.setFieldValue('destinationDistrictId', undefined);
  }, [destinationRegionId, form]);

  return (
    <Create saveButtonProps={saveButtonProps} title={t('trips.newTrip')}>
      <Form {...formProps} layout="vertical">
        <Row gutter={24}>
          {/* Trip Type and Users */}
          <Col xs={24}>
            <Card
              title={
                <SectionTitle
                  title={t('trips.tripDetails')}
                  help={t('trips.tripDetails')}
                />
              }
            >
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label={t('trips.tripType')}
                        help={t('trips.tripType')}
                      />
                    }
                    name="tripType"
                    rules={[{ required: true, message: t('trips.tripType') }]}
                    initialValue="INTERCITY_TAXI"
                  >
                    <Select placeholder={t('trips.tripType')}>
                      <Select.Option value="INTERCITY_TAXI">
                        {t('tripType.intercityTaxi')}
                      </Select.Option>
                      <Select.Option value="INTERREGION_TAXI">
                        {t('tripType.interregionTaxi')}
                      </Select.Option>
                      <Select.Option value="SHAHARLAR_ARO">
                        {t('tripType.shaharlarAro')}
                      </Select.Option>
                      <Select.Option value="YOL_YOLAKAY">
                        {t('tripType.yolYolakay')}
                      </Select.Option>
                      <Select.Option value="POSTAGE">
                        {t('tripType.postage')}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label={t('trips.client')}
                        help={t('trips.client')}
                      />
                    }
                    name="clientId"
                    rules={[{ required: true, message: t('trips.client') }]}
                  >
                    <Select
                      showSearch
                      placeholder={t('common.search')}
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={users.map((user) => ({
                        value: user.id,
                        label: `${user.fullName || t('common.noName')} (${user.phone})`,
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label={t('trips.driver')}
                        help={t('trips.driver')}
                      />
                    }
                    name="driverId"
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder={t('common.search')}
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={drivers.map((driver) => ({
                        value: driver.id,
                        label: `${driver.fullName || t('common.noName')} (${driver.phone})`,
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Pickup Location */}
          <Col xs={24} lg={12}>
            <Card
              title={
                <SectionTitle
                  title={t('trips.pickupRegion')}
                  help={t('trips.pickupAddress')}
                />
              }
            >
              <Form.Item
                label={
                  <LabelWithHelp
                    label={t('trips.pickupRegion')}
                    help={t('trips.pickupRegion')}
                  />
                }
                name="pickupRegionId"
                rules={[{ required: true, message: t('trips.pickupRegion') }]}
              >
                <Select
                  showSearch
                  placeholder={t('trips.pickupRegion')}
                  optionFilterProp="children"
                  onChange={(value) => setPickupRegionId(value)}
                  options={regions.map((region) => ({
                    value: region.id,
                    label: region.nameUz,
                  }))}
                />
              </Form.Item>

              <Form.Item
                label={
                  <LabelWithHelp
                    label={t('trips.pickupAddress')}
                    help={t('trips.pickupAddress')}
                  />
                }
                name="pickupDistrictId"
                rules={[{ required: true, message: t('trips.pickupAddress') }]}
              >
                <Select
                  showSearch
                  placeholder={t('trips.pickupAddress')}
                  optionFilterProp="children"
                  disabled={!pickupRegionId}
                  options={pickupDistricts.map((district) => ({
                    value: district.id,
                    label: district.nameUz,
                  }))}
                />
              </Form.Item>

              <Form.Item
                label={
                  <LabelWithHelp
                    label={t('trips.pickupAddress')}
                    help={t('trips.pickupAddress')}
                  />
                }
                name="pickupAddress"
                rules={[{ required: true, message: t('trips.pickupAddress') }]}
              >
                <Input placeholder="Amir Temur ko'chasi, 15-uy" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label={t('locations.lat')}
                        help={t('locations.lat')}
                      />
                    }
                    name="pickupLat"
                    rules={[{ required: true }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      step={0.000001}
                      placeholder="41.311081"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label={t('locations.lng')}
                        help={t('locations.lng')}
                      />
                    }
                    name="pickupLong"
                    rules={[{ required: true }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      step={0.000001}
                      placeholder="69.240562"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Destination Location */}
          <Col xs={24} lg={12}>
            <Card
              title={
                <SectionTitle
                  title={t('trips.destinationRegion')}
                  help={t('trips.destinationAddress')}
                />
              }
            >
              <Form.Item
                label={
                  <LabelWithHelp
                    label={t('trips.destinationRegion')}
                    help={t('trips.destinationRegion')}
                  />
                }
                name="destinationRegionId"
                rules={[{ required: true, message: t('trips.destinationRegion') }]}
              >
                <Select
                  showSearch
                  placeholder={t('trips.pickupRegion')}
                  optionFilterProp="children"
                  onChange={(value) => setDestinationRegionId(value)}
                  options={regions.map((region) => ({
                    value: region.id,
                    label: region.nameUz,
                  }))}
                />
              </Form.Item>

              <Form.Item
                label={
                  <LabelWithHelp
                    label={t('trips.destinationAddress')}
                    help={t('trips.destinationAddress')}
                  />
                }
                name="destinationDistrictId"
                rules={[{ required: true, message: t('trips.destinationAddress') }]}
              >
                <Select
                  showSearch
                  placeholder={t('trips.destinationAddress')}
                  optionFilterProp="children"
                  disabled={!destinationRegionId}
                  options={destinationDistricts.map((district) => ({
                    value: district.id,
                    label: district.nameUz,
                  }))}
                />
              </Form.Item>

              <Form.Item
                label={
                  <LabelWithHelp
                    label={t('trips.destinationAddress')}
                    help={t('trips.destinationAddress')}
                  />
                }
                name="destinationAddress"
                rules={[{ required: true, message: t('trips.destinationAddress') }]}
              >
                <Input placeholder="Registon maydoni" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label={t('locations.lat')}
                        help={t('locations.lat')}
                      />
                    }
                    name="destinationLat"
                    rules={[{ required: true }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      step={0.000001}
                      placeholder="39.654458"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label={t('locations.lng')}
                        help={t('locations.lng')}
                      />
                    }
                    name="destinationLong"
                    rules={[{ required: true }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      step={0.000001}
                      placeholder="66.959688"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Trip Details */}
          <Col xs={24}>
            <Card
              title={
                <SectionTitle
                  title={t('trips.tripDetails')}
                  help={t('trips.tripDetails')}
                />
              }
            >
              <Row gutter={16}>
                <Col xs={24} md={6}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label={t('trips.distance')}
                        help={t('trips.distance')}
                      />
                    }
                    name="distanceKm"
                    rules={[{ required: true, message: t('trips.distance') }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={1}
                      placeholder="300"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label={t('trips.estimatedTime')}
                        help={t('trips.estimatedTime')}
                      />
                    }
                    name="durationSeconds"
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={60}
                      placeholder="14400"
                    />
                  </Form.Item>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t('trips.estimatedTime')}
                  </Text>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label={t('trips.passengerCount')}
                        help={t('trips.passengerCount')}
                      />
                    }
                    name="passengerCount"
                    initialValue={1}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={1}
                      max={8}
                      placeholder="1"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    label={
                      <LabelWithHelp
                        label={t('common.price')}
                        help={t('common.price')}
                      />
                    }
                    name="price"
                    rules={[{ required: true, message: t('common.price') }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={1000}
                      step={1000}
                      placeholder="150 000"
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
                      }
                      parser={(value) =>
                        Number(value?.replace(/\s/g, '') || 0)
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item label={t('trips.paymentMethod')} name="paymentMethod" initialValue="CASH">
                    <Select>
                      <Select.Option value="CASH">{t('payment.cash')}</Select.Option>
                      <Select.Option value="UZCARD">{t('payment.uzcard')}</Select.Option>
                      <Select.Option value="HUMO">{t('payment.humo')}</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Form>
    </Create>
  );
};
